<template>
  <div class="page-shell home-mode">
    <div class="home-ambient home-ambient-left"></div>
    <div class="home-ambient home-ambient-right"></div>

    <div class="app-shell">
      <AppSidebar />

      <main v-if="loading" class="detail-empty detail-empty-contained">
        <div class="detail-card">
          <h1>正在加载课程详情</h1>
          <p>页面正在请求当前课程的真实详情数据。</p>
        </div>
      </main>

      <main v-else-if="course" class="detail-shell">
        <header class="detail-topbar">
          <button type="button" class="back-link" @click="$router.push({ name: 'home' })">
            返回课程首页
          </button>
          <div class="detail-actions">
            <span class="detail-chip">{{ course.statusText }}</span>
            <span class="detail-chip subtle">{{ course.level }}</span>
          </div>
        </header>

        <section class="detail-hero">
          <div
            class="detail-cover"
            :style="course.coverUrl ? { backgroundImage: 'url(' + course.coverUrl + ')' } : { background: course.cover }"
          >
            <div class="detail-cover-overlay">
              <span class="detail-cover-badge">共 {{ course.chapterCount }} 节课</span>
              <h1>{{ course.title }}</h1>
              <p>{{ course.description }}</p>
            </div>
          </div>

          <div class="detail-sidebar">
            <article class="detail-card teacher-card">
              <p class="detail-label">授课教师</p>
              <div class="teacher-row">
                <div class="teacher-avatar"></div>
                <div>
                  <h2>{{ course.teacherName }}</h2>
                  <p>{{ course.teacherTitle }}</p>
                </div>
              </div>
            </article>

            <article class="detail-card metric-card">
              <p class="detail-label">课程信息</p>
              <div class="metric-list">
                <div>
                  <span>总时长</span>
                  <strong>{{ course.totalDuration }}</strong>
                </div>
                <div>
                  <span>课程等级</span>
                  <strong>{{ course.level }}</strong>
                </div>
                <div>
                  <span>章节数量</span>
                  <strong>{{ course.chapterCount }}</strong>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section class="detail-grid">
          <article class="detail-card narrative-card">
            <p class="detail-label">课程简介</p>
            <p class="detail-paragraph">{{ course.longDescription }}</p>

            <div class="tag-group">
              <span v-for="tag in course.tags" :key="tag" class="mini-badge">{{ tag }}</span>
            </div>
          </article>

          <article class="detail-card goals-card">
            <p class="detail-label">学习目标</p>
            <div class="goal-list">
              <div v-for="goal in course.goals" :key="goal" class="goal-item">
                <span class="goal-dot"></span>
                <p>{{ goal }}</p>
              </div>
            </div>
          </article>
        </section>

        <section class="detail-card chapters-card">
          <div class="section-heading compact">
            <div>
              <p class="section-eyebrow">Chapters</p>
              <h2>课程章节</h2>
            </div>
          </div>

          <div class="chapter-list">
            <article v-for="chapter in course.chapters" :key="chapter.id" class="chapter-item">
              <div class="chapter-head">
                <div>
                  <p class="chapter-index">第 {{ chapter.index }} 节</p>
                  <h3>{{ chapter.title }}</h3>
                </div>
                <span class="chapter-duration">{{ chapter.duration }}</span>
              </div>

              <p class="chapter-summary">{{ chapter.summary }}</p>

              <div class="chapter-points">
                <span v-for="point in chapter.points" :key="point" class="chapter-point">{{ point }}</span>
              </div>

              <div v-if="chapter.attachments.length" class="chapter-attachments">
                <p class="chapter-attachments-title">课节附件</p>
                <div class="chapter-attachments-list">
                  <button
                    v-for="attachment in chapter.attachments"
                    :key="attachment.id"
                    type="button"
                    class="chapter-attachment-card"
                    @click="openAttachmentPreview(attachment)"
                  >
                    <div class="chapter-attachment-icon">{{ attachment.fileTypeLabel }}</div>
                    <div class="chapter-attachment-copy">
                      <strong>{{ attachment.name }}</strong>
                      <span>{{ attachment.fileTypeText }} · {{ attachment.fileSizeText }}</span>
                    </div>
                    <span class="chapter-attachment-action">{{ attachment.actionText }}</span>
                  </button>
                </div>
              </div>
            </article>

            <article v-if="!course.chapters.length" class="chapter-item chapter-item-empty">
              <div class="chapter-head">
                <div>
                  <p class="chapter-index">待补充</p>
                  <h3>当前课程还没有章节数据</h3>
                </div>
              </div>
              <p class="chapter-summary">详情接口已经接入，如果这门课暂时没有章节内容，页面会保持空状态展示。</p>
            </article>
          </div>
        </section>
      </main>

      <main v-else class="detail-empty detail-empty-contained">
        <div class="detail-card">
          <h1>{{ errorTitle }}</h1>
          <p>{{ errorMessage }}</p>
          <button type="button" class="submit-button detail-empty-button" @click="$router.push({ name: 'home' })">
            返回首页
          </button>
        </div>
      </main>

      <div
        v-if="previewAttachment"
        class="studio-lightbox"
        @click.self="closeAttachmentPreview"
      >
        <button
          type="button"
          class="studio-lightbox-close"
          @click="closeAttachmentPreview"
        >
          Close
        </button>

        <div class="studio-lightbox-panel courseware-lightbox-panel">
          <div class="courseware-preview-head">
            <div>
              <strong>{{ previewAttachment.name }}</strong>
              <span>{{ previewAttachment.fileTypeText }} · {{ previewAttachment.fileSizeText }}</span>
            </div>
            <a
              class="courseware-preview-open"
              :href="previewAttachment.openUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              新窗口打开
            </a>
          </div>

          <div class="courseware-preview-frame-wrap">
            <img
              v-if="previewAttachment.previewMode === 'image'"
              class="courseware-preview-image"
              :src="previewAttachment.previewUrl"
              :alt="previewAttachment.name"
            >
            <video
              v-else-if="previewAttachment.previewMode === 'video'"
              class="courseware-preview-video"
              :src="previewAttachment.previewUrl"
              controls
              playsinline
              preload="metadata"
            ></video>
            <iframe
              v-else
              class="courseware-preview-frame"
              :src="previewAttachment.previewUrl"
              frameborder="0"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSidebar from "../components/AppSidebar.vue";
import { refreshToken } from "../api/auth";
import { getCourseDetail } from "../api/course";
import { readCourseDashboard } from "../utils/course-dashboard";
import { clearAuthState, readAuthState, saveAuthState } from "../utils/auth";

export default {
  name: "CourseDetailView",
  components: {
    AppSidebar: AppSidebar
  },
  data: function data() {
    return {
      loading: true,
      auth: readAuthState(),
      course: createDashboardFallback(findDashboardCourse(readCourseDashboard(), this.$route.params.id)),
      previewAttachment: null,
      errorTitle: "课程不存在",
      errorMessage: "当前课程未找到，可以先返回首页重新查看。"
    };
  },
  computed: {
    courseId: function courseId() {
      return String(this.$route.params.id || "");
    }
  },
  created: function created() {
    this.bootstrapCourseDetail();
  },
  watch: {
    "$route.params.id": function routeIdChanged() {
      this.bootstrapCourseDetail();
    }
  },
  methods: {
    async bootstrapCourseDetail() {
      this.loading = true;
      this.auth = readAuthState();
      this.previewAttachment = null;
      this.errorTitle = "课程不存在";
      this.errorMessage = "当前课程未找到，可以先返回首页重新查看。";
      this.course = createDashboardFallback(findDashboardCourse(readCourseDashboard(), this.courseId));

      if (!this.auth.accessToken) {
        this.handleUnauthorized();
        return;
      }

      try {
        var payload = await getCourseDetail(this.courseId, this.auth.accessToken);
        this.course = normalizeCourseDetail(unwrapCourseDetailPayload(payload));
      } catch (error) {
        if (error.status === 401) {
          var refreshed = await this.tryRefresh();
          if (!refreshed) {
            this.handleUnauthorized();
            return;
          }
        } else if (error.status === 404) {
          this.course = null;
          this.errorTitle = "课程不存在";
          this.errorMessage = error.message || "Course not found.";
        } else {
          this.course = this.course || null;
          this.errorTitle = "课程详情加载失败";
          this.errorMessage = error.message || "请稍后重试。";
        }
      } finally {
        this.loading = false;
      }
    },
    async tryRefresh() {
      try {
        var payload = await refreshToken(this.auth.accessToken);
        this.auth = saveAuthState({
          accessToken: payload.access_token,
          tokenType: payload.token_type,
          expiresIn: payload.expires_in,
          user: payload.user
        });
        var detail = await getCourseDetail(this.courseId, this.auth.accessToken);
        this.course = normalizeCourseDetail(unwrapCourseDetailPayload(detail));
        return true;
      } catch (error) {
        return false;
      }
    },
    handleUnauthorized: function handleUnauthorized() {
      clearAuthState();
      this.$router.replace({ name: "login" });
    },
    openAttachmentPreview: function openAttachmentPreview(attachment) {
      if (!attachment || !attachment.previewUrl) {
        return;
      }

      if (attachment.previewMode === "external") {
        window.open(attachment.openUrl || attachment.previewUrl, "_blank", "noopener,noreferrer");
        return;
      }

      this.previewAttachment = attachment;
    },
    closeAttachmentPreview: function closeAttachmentPreview() {
      this.previewAttachment = null;
    }
  }
};

function findDashboardCourse(dashboard, id) {
  var source = dashboard || {};
  var courses = Array.isArray(source.courses) ? source.courses : [];

  if (source.featuredCourse && String(source.featuredCourse.id) === String(id)) {
    return source.featuredCourse;
  }

  return courses.find(function(course) {
    return String(course.id) === String(id);
  }) || null;
}

function createDashboardFallback(course) {
  if (!course) {
    return null;
  }

  return {
    id: course.id || 0,
    title: course.title || "未命名课程",
    description: course.description || "",
    longDescription: course.description || "课程详情正在加载中。",
    chapterCount: Number(course.chapterCount || 0),
    totalDuration: "待加载",
    level: "待加载",
    teacherName: "待加载",
    teacherTitle: "课程详情加载后显示",
    status: course.status || "not_started",
    statusText: course.statusText || "未开始",
    coverUrl: normalizeCoverUrl(course.coverUrl || ""),
    cover: "linear-gradient(160deg, #d9cbff 0%, #9b79ff 48%, #5f33dd 100%)",
    tags: [],
    goals: [],
    chapters: []
  };
}

function unwrapCourseDetailPayload(payload) {
  if (payload && payload.data && typeof payload.data === "object") {
    return payload.data;
  }

  return payload || {};
}

function normalizeCourseDetail(payload) {
  var source = payload || {};

  return {
    id: source.id || 0,
    title: source.title || "未命名课程",
    description: source.description || "",
    longDescription: source.longDescription || source.description || "",
    chapterCount: Number(source.chapterCount || 0),
    totalDuration: source.totalDuration || "未提供",
    level: source.level || "未提供",
    teacherName: source.teacherName || "未提供",
    teacherTitle: source.teacherTitle || "未提供",
    status: source.status || "not_started",
    statusText: source.statusText || "未开始",
    coverUrl: normalizeCoverUrl(source.coverUrl || ""),
    cover: "linear-gradient(160deg, #d9cbff 0%, #9b79ff 48%, #5f33dd 100%)",
    tags: normalizeStringList(source.tags),
    goals: normalizeStringList(source.goals),
    chapters: normalizeChapters(source.chapters)
  };
}

function normalizeStringList(payload) {
  return Array.isArray(payload) ? payload.filter(Boolean) : [];
}

function normalizeChapters(payload) {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map(function(chapter, index) {
    var source = chapter || {};
    return {
      id: source.id || "chapter-" + index,
      index: Number(source.index || index + 1),
      title: source.title || "未命名章节",
      duration: source.duration || "未提供",
      summary: source.summary || "",
      points: normalizeStringList(source.points),
      attachments: normalizeAttachments(source.attachments || source.attach)
    };
  });
}

function normalizeAttachments(payload) {
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.map(function(attachment, index) {
    var source = attachment || {};
    var fileUrl = source.fileUrl || source.file_url || "";
    var fileType = String(source.fileType || source.file_type || "").toLowerCase().trim();
    var fileName = source.name || "";
    var resolvedType = resolveAttachmentFieldType(fileType) || resolveAttachmentExtension("", fileUrl, fileName);

    if (!fileUrl) {
      return null;
    }

    return {
      id: source.id || "attachment-" + index,
      name: source.name || "未命名附件",
      fileType: resolvedType,
      fileTypeText: resolveAttachmentTypeText(resolvedType),
      fileTypeLabel: resolveAttachmentTypeLabel(resolvedType),
      fileUrl: fileUrl,
      fileSizeText: source.fileSize || source.file_size || "未知大小",
      previewMode: resolveAttachmentPreviewMode(resolvedType),
      previewUrl: resolveAttachmentPreviewUrl(resolvedType, fileUrl),
      openUrl: resolveAttachmentOpenUrl(resolvedType, fileUrl),
      actionText: resolveAttachmentActionText(resolvedType)
    };
  }).filter(Boolean);
}

function resolveAttachmentTypeText(fileType) {
  if (!fileType) {
    return "附件";
  }

  if (fileType === "ppt" || fileType === "pptx") {
    return "PowerPoint";
  }

  if (fileType === "doc" || fileType === "docx" || fileType === "word") {
    return "Word";
  }

  if (fileType === "xls" || fileType === "xlsx" || fileType === "excel") {
    return "Excel";
  }

  if (fileType === "pdf") {
    return "PDF";
  }

  if (isExternalAttachmentType(fileType)) {
    return "外部链接";
  }

  return fileType.toUpperCase();
}

function resolveAttachmentTypeLabel(fileType) {
  if (!fileType) {
    return "FILE";
  }

  if (isExternalAttachmentType(fileType)) {
    return "LINK";
  }

  return String(fileType).replace(/[^a-z0-9]/ig, "").toUpperCase().slice(0, 4) || "FILE";
}

function resolveAttachmentPreviewMode(fileType) {
  if (isImageAttachmentType(fileType)) {
    return "image";
  }

  if (isVideoAttachmentType(fileType)) {
    return "video";
  }

  if (isExternalAttachmentType(fileType)) {
    return "external";
  }

  return "office";
}

function resolveAttachmentPreviewUrl(fileType, fileUrl) {
  var mode = resolveAttachmentPreviewMode(fileType);

  if (mode === "image" || mode === "video" || mode === "external") {
    return fileUrl;
  }

  return buildOfficePreviewUrl(fileUrl);
}

function resolveAttachmentOpenUrl(fileType, fileUrl) {
  var mode = resolveAttachmentPreviewMode(fileType);

  if (mode === "image" || mode === "video" || mode === "external") {
    return fileUrl;
  }

  return buildOfficePreviewUrl(fileUrl);
}

function resolveAttachmentActionText(fileType) {
  if (isExternalAttachmentType(fileType)) {
    return "打开链接";
  }

  return "在线预览";
}

function buildOfficePreviewUrl(fileUrl) {
  return "https://view.officeapps.live.com/op/embed.aspx?src=" + encodeURIComponent(fileUrl);
}

function resolveAttachmentExtension(fileType, fileUrl, fileName) {
  var normalizedType = String(fileType || "").toLowerCase().trim();

  if (normalizedType) {
    if (normalizedType.indexOf("/") > -1) {
      var mimeMatch = normalizedType.match(/(?:image|video)\/([a-z0-9.+-]+)/i);
      if (mimeMatch && mimeMatch[1]) {
        return normalizeAttachmentSubtype(mimeMatch[1]);
      }
    }

    return normalizeAttachmentSubtype(normalizedType);
  }

  var cleanName = String(fileName || "").trim();
  var nameMatch = cleanName.match(/\.([a-z0-9]+)$/i);

  if (nameMatch) {
    return normalizeAttachmentSubtype(nameMatch[1]);
  }

  var cleanUrl = String(fileUrl || "").split("?")[0];
  var match = cleanUrl.match(/\.([a-z0-9]+)$/i);

  return match ? normalizeAttachmentSubtype(match[1]) : "";
}

function normalizeAttachmentSubtype(value) {
  var normalized = String(value || "").toLowerCase().trim();

  if (normalized === "jpg") {
    return "jpeg";
  }

  if (normalized === "svg+xml") {
    return "svg";
  }

  if (normalized === "quicktime") {
    return "mov";
  }

  return normalized;
}

function resolveAttachmentFieldType(fileType) {
  var normalizedType = String(fileType || "").toLowerCase().trim();

  if (!normalizedType) {
    return "";
  }

  if (normalizedType.indexOf("/") > -1) {
    var mimeMatch = normalizedType.match(/(?:image|video)\/([a-z0-9.+-]+)/i);
    if (mimeMatch && mimeMatch[1]) {
      return normalizeAttachmentSubtype(mimeMatch[1]);
    }
  }

  return normalizeAttachmentSubtype(normalizedType);
}

function isImageAttachmentType(fileType) {
  return ["image", "jpeg", "png", "gif", "bmp", "webp", "svg"].indexOf(fileType) > -1;
}

function isVideoAttachmentType(fileType) {
  return ["video", "mp4", "webm", "ogg", "mov", "m4v"].indexOf(fileType) > -1;
}

function isExternalAttachmentType(fileType) {
  return ["link", "url", "external", "external_link", "external-link"].indexOf(fileType) > -1;
}

function normalizeCoverUrl(url) {
  if (!url) {
    return "";
  }

  return url;
}
</script>
